import {
  Add,
  CalendarToday as CalendarIcon,
  CheckCircle,
  CircleOutlined,
  FilterList as FilterIcon,
  People as PeopleIcon,
  Restaurant as RestaurantIcon,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Divider,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
  useTheme,
} from '@mui/material';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useIsMobile } from '../../../hooks/useIsMobile';
import { useMyChecksContext } from '../../../hooks/useMyChecksContext';
import { FULLY_CLAIMED_COLOR } from '../../../theme';
import { $ } from '../../../util/formatCurrency.util';

interface MySplitsProps {
  type: 'all' | 'recent';
}

export const MyChecks: React.FC<MySplitsProps> = ({ type }) => {
  const limit = type === 'all' ? undefined : 5;
  const title = type === 'all' ? '🧾 My Splits' : '🧾 My Recent Splits';
  const buttonType = type === 'all' ? 'create-new' : 'view-all';

  const { myChecks } = useMyChecksContext();
  const theme = useTheme();
  const isMobile = useIsMobile();

  const [filters, setFilters] = useState<string[]>([]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleFilterClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setAnchorEl(null);
  };

  const handleFilterChange = (filter: string) => {
    setFilters((prev) => (prev.includes(filter) ? prev.filter((f) => f !== filter) : [...prev, filter]));
  };

  const filteredChecks = myChecks.filter((check) => {
    if (filters.length === 0) return true;
    return filters.every((filter) => {
      if (filter === 'my-checks') return check.isOwner;
      if (filter === 'not-fully-claimed') return check.percentClaimed < 100;
      return false;
    });
  });

  return (
    <Card>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ pb: 2 }}>
          <Typography variant="h6">{title}</Typography>
          {buttonType === 'create-new' && (
            <Box display="flex" alignItems="center">
              <Button
                variant="contained"
                color="primary"
                startIcon={<Add />}
                component={Link}
                to="/new"
                sx={{
                  mr: 1,
                  fontWeight: { xs: 'bold', sm: 'bold' },
                }}
              >
                New Split
              </Button>
              <IconButton
                onClick={handleFilterClick}
                size="small"
                sx={{
                  bgcolor: filters.length > 0 ? theme.palette.primary.main : 'transparent',
                  color: filters.length > 0 ? theme.palette.primary.contrastText : theme.palette.action.active,
                  '&:hover': {
                    bgcolor: filters.length > 0 ? theme.palette.primary.dark : theme.palette.action.hover,
                  },
                }}
              >
                <FilterIcon />
              </IconButton>
              <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleFilterClose}>
                <MenuItem onClick={() => handleFilterChange('my-checks')}>
                  <Checkbox
                    checked={filters.includes('my-checks')}
                    onChange={(e) => {
                      e.stopPropagation();
                      handleFilterChange('my-checks');
                    }}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <Typography
                    component="span"
                    variant="caption"
                    sx={{
                      ml: 1,
                      px: 1,
                      py: 0.5,
                      bgcolor: theme.palette.secondary.main,
                      color: theme.palette.secondary.contrastText,
                      borderRadius: 1,
                    }}
                  >
                    My Bills
                  </Typography>
                </MenuItem>
                <MenuItem onClick={() => handleFilterChange('not-fully-claimed')}>
                  <Checkbox
                    checked={filters.includes('not-fully-claimed')}
                    onChange={(e) => {
                      e.stopPropagation();
                      handleFilterChange('not-fully-claimed');
                    }}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <Typography
                    component="span"
                    variant="caption"
                    sx={{
                      ml: 1,
                      px: 1,
                      py: 0.5,
                      bgcolor: FULLY_CLAIMED_COLOR,
                      color: theme.palette.getContrastText(FULLY_CLAIMED_COLOR),
                      borderRadius: 1,
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    Not Fully Claimed
                    <CircleOutlined sx={{ ml: 0.5 }} />
                  </Typography>
                </MenuItem>
              </Menu>
            </Box>
          )}
          {buttonType === 'view-all' && (
            <Button component={Link} to="/checks" variant="outlined">
              View All
            </Button>
          )}
        </Box>

        {filteredChecks.length === 0 && (
          <Box display="flex" flexDirection="column" alignItems="center" py={4}>
            <Typography variant="h5" gutterBottom>
              🍽️
            </Typography>
            <Typography variant="body1" color="text.secondary" align="center">
              {filters.length > 0
                ? 'No splits found for the selected filters.'
                : 'You don’t have any Splits yet. Start by creating a new bill!'}
            </Typography>
          </Box>
        )}
        <List disablePadding>
          {filteredChecks.slice(0, limit).map((check) => (
            <React.Fragment key={check.id}>
              <Divider component="li" />
              <ListItem disablePadding>
                <ListItemButton component={Link} to={`/checks/${check.id}`}>
                  {!isMobile && (
                    <ListItemIcon>
                      <RestaurantIcon color="primary" />
                    </ListItemIcon>
                  )}
                  <ListItemText
                    primaryTypographyProps={{ component: 'div' }}
                    secondaryTypographyProps={{ component: 'div' }}
                    primary={
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Box>
                          <Typography variant="subtitle1" component="span">
                            {check.restaurantName}
                          </Typography>
                          {check.isOwner ? (
                            <Typography
                              component="span"
                              variant="caption"
                              sx={{
                                ml: 1,
                                px: 1,
                                py: 0.5,
                                bgcolor: theme.palette.secondary.main,
                                color: theme.palette.secondary.contrastText,
                                borderRadius: 1,
                              }}
                            >
                              My Bill
                            </Typography>
                          ) : (
                            <>
                              <Typography
                                component="span"
                                variant="caption"
                                sx={{
                                  ml: 1,
                                  color: theme.palette.text.secondary,
                                }}
                              >
                                •
                              </Typography>
                              <Typography
                                component="span"
                                variant="caption"
                                sx={{
                                  ml: 1,
                                  color: theme.palette.text.secondary,
                                }}
                              >
                                {check.ownerName}'s Bill
                              </Typography>
                            </>
                          )}
                        </Box>
                        <Typography variant="subtitle1" fontWeight="bold" color="primary">
                          {$(check.totalPrice)}
                        </Typography>
                      </Box>
                    }
                    secondary={
                      <>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Typography variant="body2" color="text.secondary">
                            <CalendarIcon
                              sx={{
                                fontSize: 'small',
                                mr: 0.5,
                                verticalAlign: 'text-bottom',
                                color: theme.palette.primary.light,
                              }}
                            />
                            {check.formattedDate}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            <PeopleIcon
                              sx={{
                                fontSize: 'small',
                                mr: 0.5,
                                verticalAlign: 'text-bottom',
                                color: theme.palette.primary.light,
                              }}
                            />
                            {check.splitCount} people
                          </Typography>
                        </Box>
                        <Box display="flex" alignItems="center" mt={1}>
                          <Box flexGrow={1} mr={2}>
                            <LinearProgress
                              variant="determinate"
                              value={check.percentClaimed}
                              sx={{
                                height: 4,
                                borderRadius: 2,
                                backgroundColor: theme.palette.grey[200],
                                '& .MuiLinearProgress-bar': {
                                  backgroundColor:
                                    check.percentClaimed === 100 ? FULLY_CLAIMED_COLOR : theme.palette.secondary.main,
                                },
                              }}
                            />
                          </Box>
                          <Box display="flex" alignItems="center">
                            <Typography variant="body2">{check.percentClaimed}% claimed</Typography>
                            {check.percentClaimed === 100 && (
                              <CheckCircle
                                sx={{
                                  color: FULLY_CLAIMED_COLOR,
                                  fontSize: 'small',
                                  ml: 0.5,
                                }}
                              />
                            )}
                          </Box>
                        </Box>
                      </>
                    }
                  />
                </ListItemButton>
              </ListItem>
            </React.Fragment>
          ))}
        </List>
      </CardContent>
    </Card>
  );
};
